export const tiers = [
  {
    id: 'launch',
    name: 'Launch',
    for: 'For businesses ready to stop renting their online presence.',
    price: 'Starting from $75',
    usdPrice: '~₦100,000',
    description:
      'A real website, not a template, for startups, personal brands, events and businesses launching online for the first time.',
    examples: ['Landing Pages', 'One-page Websites', 'Portfolio Websites', 'Event Websites'],
    cta: 'Get Started',
    featured: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    badge: 'Most Popular',
    for: 'For businesses ready to turn visitors into enquiries.',
    price: 'Starting from $220',
    usdPrice: '~₦300,000',
    description:
      'Built for businesses ready to be found, trusted, and chosen, without depending on an algorithm to get there. Every page is structured with keyword-informed content and on-page SEO from launch, not added later. Includes full Google Business Profile and Search Console setup, plus a one-time review of your existing social presence.',
    examples: ['Business Websites', 'Service Companies', 'Fashion Brands', 'Schools', 'Restaurants', 'Corporate Websites'],
    cta: 'Get a Free Website Audit',
    featured: true,
  },
  {
    id: 'scale',
    name: 'Scale',
    for: 'For businesses that have outgrown a standard website.',
    price: 'Starting from $500',
    usdPrice: '~₦675,000',
    description:
      'A custom platform built around what your business actually needs, not what a template allows. Includes everything in Growth, plus full website copywriting, deeper SEO, and a full social + platform strategy review. Genuinely bespoke builds — memberships, customer dashboards, complex automations — start from $1,000 (~₦1.35m), quoted based on requirements.',
    examples: ['E-commerce', 'Booking Systems', 'Payment Integrations', 'Advanced SEO Builds'],
    cta: "Let's Talk",
    featured: false,
  },
]

// Pros/cons comparison table for the pricing page — one row per
// dimension bv locked in, in the same order agreed. `true`/`false`
// render as a checkmark/dash; strings render as-is for dimensions where
// a plain checkmark isn't enough (e.g. Google Business Profile is
// "guidance only" at Launch but "full setup" at Growth/Scale — both are
// technically "included," just very differently, so a bare checkmark
// would be misleading there).
export const tierComparison = [
  { label: 'Pages', values: ['1–3', '4–7', '8+'] },
  { label: 'On-page SEO foundation', values: [true, true, true] },
  { label: 'Keyword-informed content', values: [false, true, true] },
  { label: 'Google Business Profile', values: ['Guidance only', 'Full setup', 'Full setup'] },
  { label: 'Google Search Console setup', values: [false, true, true] },
  { label: 'Social presence review', values: [false, 'Basic (one-time)', 'Full, with content calendar (one-time)'] },
  { label: 'Website copywriting', values: [false, false, 'Full copywriting'] },
  { label: 'Deeper SEO (schema, tracking)', values: [false, false, true] },
  { label: 'Ecommerce / booking / payments', values: [false, false, true] },
]

export const faqs = [
  {
    q: 'Why do I need a website if I already use Instagram or WhatsApp?',
    a: "You don't own Instagram or WhatsApp. You're borrowing space on someone else's platform. A website is the one part of your business online that's actually yours, and the first place serious customers check before they trust you.",
  },
  {
    q: 'How much does a website cost?',
    a: "Every project is different, so our pricing starts from $75 (~₦100,000). We'll recommend the best solution based on your goals and provide a transparent quote.",
  },
  {
    q: 'Can I pay in installments?',
    a: "Yes. Most projects run on a 70% deposit to begin and 30% on completion. Larger or custom projects can be staged further where it makes sense.",
  },
  {
    q: 'How long does a typical project take?',
    a: "Most websites are completed within 1–3 weeks, depending on the project's scope and how quickly content and feedback are provided.",
  },
  {
    q: 'Will my website work on mobile devices?',
    a: "Yes. Most of your visitors will meet you on a phone before they ever open a laptop, so every site is designed for that screen first, then scaled up.",
  },
  {
    q: 'Can you redesign my existing website?',
    a: 'Yes. We can redesign outdated or underperforming websites into modern, faster, and conversion-focused experiences.',
  },
  {
    q: 'Can I add more features as my business grows?',
    a: 'Definitely. Your website is built to grow with your business, making it easy to add new pages, features, or integrations over time.',
  },
  {
    q: 'Do you also provide branding and logo design?',
    a: 'Yes. We can help with logos, brand identity, visual direction, and other design assets to ensure your business looks professional across every touchpoint.',
  },
  {
    q: 'Can I sell products or accept payments on my website?',
    a: 'Yes. We build websites with e-commerce, online payments, bookings, and other custom functionality based on your business needs.',
  },
  {
    q: 'Do I need to buy a domain name and hosting?',
    a: "Yes, but don't worry. We'll guide you through the process and help you get everything set up correctly.",
  },
  {
    q: 'Where are you based, and do you work internationally?',
    a: "Based in Nigeria. Working with businesses anywhere. Calls, payments, and handover are all set up to work smoothly no matter the time zone.",
  },
  {
    q: 'What do you need from me before we begin?',
    a: "Just a few basics about your business, your goals, and any existing branding or content. If you're starting from scratch, we'll guide you every step of the way.",
  },
  {
    q: 'What happens after my website goes live?',
    a: "We'll make sure everything is running smoothly and provide guidance on managing your website. We're also available for future updates and improvements.",
  },
  {
    q: 'What makes Better Website Co. different?',
    a: "Most agencies sell you a website. We think about what happens after: the enquiry that comes in at 11pm, the customer who Googles you before replying to your DM. Every site we build exists to make that moment work in your favor.",
  },
  {
    q: 'Will my website actually show up in Google searches?',
    a: "Yes, but not instantly; no one can promise that, and treat it as a red flag if they do. Every Growth and Scale build includes keyword-informed content and on-page SEO from launch, so the foundation is there from day one, and your site typically gets indexed by Google within days to a couple of weeks. Actual ranking takes longer: most new websites see initial movement around 3–6 months in, with meaningful rankings on competitive search terms usually taking 6–12 months. That's normal across the industry, not a sign something's wrong. Ranking also compounds over time rather than happening once at launch, which is what our Growth Plans (available after launch) are built for.",
  },
]
